<!-- eslint-disable vue/html-self-closing -->
<template>
  <section
    class="merge-request-header"
    :class="[{ '--actions': hasEditAccess }, { '--alert': mergeRequest.sourceBeforeTargetCommitCount > 0 }]"
  >
    <div class="merge-request-header__header">
      <div class="additional">
        <merge-request-ci-pipeline-status v-if="isCiCdEnabled" />
        <b class="id">#{{ mergeRequest.localId }}</b>
      </div>
      <div class="title">
        <i
          v-if="mergeRequest.workInProgress"
          class="gf-icon gf-icon-lock gf-fs-4"
        ></i>
        <h4>
          <span>{{ mergeRequest.title || $t('message.title_empty') }}</span>
        </h4>
      </div>
    </div>
    <status-label
      class="merge-request-header__status"
      :status="mergeRequest.status"
    />
    <div
      v-if="mergeRequest?.createdBy"
      class="merge-request-header__author"
    >
      <div class="author">
        <a :href="authorLink">
          <img
            :src="mergeRequest.createdBy?.avatar"
            class="avatar"
            height="22px"
            width="22px"
          />
        </a>
        <a
          class="username truncated-link"
          :href="authorLink"
        >
          <b>{{ mergeRequest.createdBy?.fullName }}</b>
        </a>
      </div>
      <div class="branch">
        {{ $t('wants_to_merge_branch') }}
        <span v-if="mergeRequest.sourceBranch?.isDeleted">
          {{ mergeRequest.sourceBranch?.title }}
        </span>
        <a
          v-else
          class="truncated-link-long"
          :href="sourceBranchLink"
        >
          {{ sourceBranchText }}
        </a>
        <span>{{ $tc('message.branch', 2) }}</span>
        <span v-if="mergeRequest.targetBranch?.isDeleted">
          {{ mergeRequest.targetBranch.title }}
        </span>
        <a
          v-else
          class="truncated-link"
          :href="targetBranchLink"
        >
          {{ mergeRequest.targetBranch?.title }}
        </a>
      </div>
    </div>
    <div
      v-if="getActionAvailabilityFlag(ACTION_ENUM.READ_DISCUSSION)"
      class="merge-request-header__comments"
    >
      <i class="gf-icon gf-icon-comment"></i>
      <small>{{ mergeRequest?.discussionCount }}</small>
    </div>
    <div
      class="merge-request-header__alert gf-badge-branch-warning"
      v-if="mergeRequest.sourceBeforeTargetCommitCount > 0"
    >
      {{ $t('branch_source') }}
      {{ $t('lags_behind') }}
      <template v-if="mergeRequest.sourceBeforeTargetCommitCount > 20">20+</template>
      <template v-else>{{ mergeRequest.sourceBeforeTargetCommitCount }}</template>
      {{ $pluralize(parseInt(mergeRequest.sourceBeforeTargetCommitCount), $t('message.commits_counted')) }}
    </div>
    <div
      v-if="hasEditAccess"
      class="merge-request-header__actions"
    >
      <gf-link-button
        :href="editLink"
        variant="outline-primary"
        :text="$t('message.edit')"
        after-icon="gf-icon gf-icon-pen"
      />
    </div>
  </section>
</template>

<script>
// eslint-disable-next-line max-len
import MergeRequestCiPipelineStatus from '@shared/modules/merge-request/components/item/MergeRequestCiPipelineStatus';
import ACTION_ENUM from '@shared/modules/custom-roles/custom-roles.constants';
import StatusLabel from '@shared/shared-components/status-label';
import mergeRequestMessages from '@shared/modules/merge-request/mergeRequestMessages';
import GfLinkButton from '@/shared-modules/shared-components/shared/components/GfLinkButton';

export default {
  name: 'MergeRequestHeader',
  components: {
    MergeRequestCiPipelineStatus,
    StatusLabel,
    GfLinkButton
  },
  data() {
    return {};
  },
  computed: {
    mergeRequest() {
      return this.$store.getters.getSelectedMergeRequest || {};
    },
    isCiCdEnabled() {
      return this.$store.getters.getCicdStatus && this.getActionAvailabilityFlag(ACTION_ENUM.READ_CI_CD);
    },
    ACTION_ENUM() {
      return ACTION_ENUM;
    },
    authorLink() {
      return `/user/${this.mergeRequest?.createdBy?.username}`;
    },
    sourceBranchLink() {
      return `/project/${this.mergeRequest.sourceProject?.ownerAlias}/${this.mergeRequest.sourceProject?.alias}?branch=${this.mergeRequest.sourceBranch?.id}`;
    },
    sourceBranchText() {
      return `${this.mergeRequest.sourceProject?.ownerAlias}/${this.mergeRequest.sourceProject?.alias} - ${this.mergeRequest.sourceBranch?.title}`;
    },
    targetBranchLink() {
      return `/project/${this.$route.params.alias}/${this.$route.params.projectAlias}?branch=${this.mergeRequest.targetBranch?.id}`;
    },
    hasEditAccess() {
      return document.getElementById('hasEditAccess')?.getAttribute('value') === 'true';
    },
    editLink() {
      return `/project/${this.$route.params.alias}/${this.$route.params.projectAlias}/merge-request/${this.$route.params.id}/edit`;
    }
  },
  methods: {
    getActionAvailabilityFlag(action) {
      return this.$store.getters.getActionAvailabilityFlag(action);
    }
  },
  i18n: {
    messages: mergeRequestMessages
  }
};
</script>

<style lang="scss">
$this: '.merge-request-header';

#{$this} {
  display: grid;
  grid-template-columns: min-content min-content auto;
  grid-template-areas:
    'header header header'
    'status author comments';
  align-items: center;
  gap: 12px;

  @include theme.max-xl {
    grid-template-columns: min-content min-content auto;
    grid-template-areas:
      'header header header'
      'status author comments';
  }

  @include theme.max-sm {
    grid-template-columns: auto min-content;
    grid-template-areas:
      'header header'
      'status comments'
      'author author';

    gap: 16px;
  }

  &.--actions {
    grid-template-columns: min-content min-content auto min-content;
    grid-template-areas:
      'header header header actions'
      'status author comments comments';

    @include theme.max-xl {
      grid-template-columns: min-content auto min-content min-content;
      grid-template-areas:
        'header header header header'
        'status author author comments'
        'actions actions actions actions';
    }

    @include theme.max-sm {
      grid-template-columns: auto min-content;
      grid-template-areas:
        'header header'
        'status comments'
        'author author'
        'alert alert';
    }
  }

  &.--alert {
    grid-template-columns: min-content min-content min-content auto;
    grid-template-areas:
      'header header header header'
      'status author comments alert';

    @include theme.max-xl {
      grid-template-columns: min-content min-content auto;
      grid-template-areas:
        'header header header'
        'status author comments'
        'alert alert alert';
    }

    @include theme.max-sm {
      grid-template-columns: auto min-content;
      grid-template-areas:
        'header header'
        'status comments'
        'author author'
        'actions actions';
    }
  }

  &.--actions.--alert {
    grid-template-columns: min-content min-content min-content auto min-content;
    grid-template-areas:
      'header header header header actions'
      'status author comments alert alert';

    @include theme.max-xl {
      grid-template-columns: min-content auto min-content min-content;
      grid-template-areas:
        'header header header header'
        'status author author comments'
        'alert alert actions actions';
    }

    @include theme.max-sm {
      grid-template-columns: auto min-content;
      grid-template-areas:
        'header header'
        'status comments'
        'author author'
        'alert alert'
        'actions actions';
    }
  }

  #{$this}__header {
    grid-area: header;
    display: flex;
    align-items: flex-start;
    gap: 8px;

    @include theme.max-sm {
      flex-direction: column;
      gap: 4px;
    }

    & > .additional {
      display: flex;
      align-items: flex-start;
      gap: 8px;

      & > .id {
        line-height: 1.2;
        font-size: 24px;
        color: var(--text-muted);
      }
    }

    & > .title {
      display: flex;
      gap: 4px;
      align-items: flex-start !important;
      word-break: break-word;

      & > i {
        min-height: 28px;
      }

      & > h4 {
        margin-bottom: 0;
      }
    }
  }

  #{$this}__status {
    grid-area: status;
    min-height: 24px;
  }

  #{$this}__author {
    grid-area: author;
    display: inline-flex;
    align-items: baseline;
    gap: 4px;
    white-space: nowrap;
    color: var(--text-muted);

    @include theme.max-lg {
      flex-wrap: wrap;
    }

    & > .author {
      display: inline-flex;
      align-items: baseline;
      gap: 4px;

      & .avatar {
        border-radius: 11px;
      }

      & > .username {
        color: var(--text-muted);
      }
    }

    & > .branch {
      display: inline-flex;
      gap: 4px;
      font-size: 12px;

      @include theme.max-lg {
        flex-wrap: wrap;
      }
    }
    & .truncated-link {
      display: inline-block;
      max-width: 210px;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;

      @include theme.max-xxl {
        max-width: 150px;
      }

      @include theme.max-xl {
        max-width: 140px;
      }

      @include theme.max-lg {
        max-width: 300px;
      }

      @include theme.max-md {
        max-width: 340px;
      }

      @include theme.max-sm {
        max-width: 240px;
      }
    }

    & .truncated-link-long {
      display: inline-block;
      max-width: 270px;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;

      @include theme.max-xxl {
        max-width: 152px;
      }

      @include theme.max-xl {
        max-width: 220px;
      }

      @include theme.max-lg {
        max-width: 300px;
      }

      @include theme.max-md {
        max-width: 386px;
      }

      @include theme.max-sm {
        max-width: 285px;
      }
    }
  }

  #{$this}__comments {
    grid-area: comments;

    @include theme.max-xl {
      margin-left: auto;
    }

    & > i {
      margin-right: 4px;
    }
  }
  #{$this}__alert {
    margin-left: 12px;
    grid-area: alert;

    @include theme.max-xl {
      margin-left: 0;
    }
  }
  #{$this}__actions {
    grid-area: actions;
    margin-left: auto;
    margin-bottom: auto;

    @include theme.max-md {
      margin-left: unset;
    }

    & > a {
      @include theme.max-md {
        width: 100%;
      }
    }
  }
}
</style>
